<template>
  <div class="bed-size-form">
    <b-card>
      <template #header>
        <h2 class="mb-0">{{ isEdit ? $t('bed_sizes.edit_title') : $t('bed_sizes.create_title') }}</h2>
      </template>
      <b-form @submit.prevent="handleSubmit" :validated="validated" novalidate>
        <b-form-group
          :label="$t('bed_sizes.form.name_label')"
          label-for="name"
          :state="!hasError('name')"
          :invalid-feedback="getError('name')"
        >
          <b-form-input
            id="name"
            v-model="formFields.name"
            :state="!hasError('name')"
            required
            @input="removeError('name')"
          ></b-form-input>
        </b-form-group>
        <b-form-group
          :label="$t('bed_sizes.form.status_label')"
          label-for="is_active"
          :state="!hasError('is_active')"
          :invalid-feedback="getError('is_active')"
        >
          <b-form-select
            id="is_active"
            v-model="formFields.is_active"
            :options="statusOptions"
            :state="!hasError('is_active')"
            required
            @input="removeError('is_active')"
          ></b-form-select>
        </b-form-group>
        <div class="d-flex gap-2 mt-4">
          <b-button type="submit" variant="primary" :disabled="isSubmitting">
            <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
            {{ isSubmitting ? $t('common.saving') : $t('common.save') }}
          </b-button>
          <b-button variant="secondary" @click="cancel">{{ $t('common.cancel') }}</b-button>
        </div>
        <div v-if="formLevelError" class="mt-3">
          <b-alert show variant="danger" class="mb-0">
            {{ formLevelError }}
          </b-alert>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import NotifyMixin from "@/Util/NotifyMixin";

export default {
  name: "BedSizeForm",
  mixins: [NotifyMixin],
  props: {
    id: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      isSubmitting: false,
      validated: false,
      formErrors: {},
      formLevelError: "",
      formFields: {
        name: "",
        is_active: 1,
      },
      statusOptions: [
        { value: 1, text: this.$t("bed_sizes.status_active") },
        { value: 0, text: this.$t("bed_sizes.status_inactive") },
      ],
    };
  },
  computed: {
    isEdit() {
      return !!this.id;
    },
  },
  methods: {
    hasError(field) {
      return !!this.formErrors[field];
    },
    getError(field) {
      return this.formErrors[field] ? this.formErrors[field][0] : "";
    },
    removeError(field) {
      if (this.formErrors[field]) {
        delete this.formErrors[field];
      }
      this.formLevelError = "";
    },
    async fetchBedSize() {
      try {
        const response = await request({
          method: "get",
          url: `/bed-sizes/${this.id}`,
        });
        this.formFields = {
          ...this.formFields,
          ...response.data,
        };
      } catch (error) {
        this.formLevelError = error.response?.data?.message || this.$t("bed_sizes.fetch_error");
        this.notifyError(this.formLevelError);
        this.$router.push("/admin/bed-sizes");
      }
    },
    async handleSubmit() {
      try {
        this.validated = true;
        this.isSubmitting = true;
        this.formErrors = {};
        this.formLevelError = "";
        if (!this.formFields.name.trim()) {
          this.formErrors.name = [this.$t("bed_sizes.validation.name_required")];
        }
        if (Object.keys(this.formErrors).length > 0) {
          throw new Error("Validation failed");
        }
        await request({
          method: this.isEdit ? "put" : "post",
          url: this.isEdit ? `/bed-sizes/${this.id}` : "/bed-sizes",
          data: this.formFields,
        });
        this.notifySuccess(
          this.isEdit ? this.$t("bed_sizes.update_success") : this.$t("bed_sizes.create_success")
        );
        this.$router.push("/admin/bed-sizes");
      } catch (error) {
        if (error.status === 422) {
          this.formErrors = error.data.message || {};
          this.formLevelError = this.formErrors;
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    cancel() {
      this.$router.push("/admin/bed-sizes");
    },
  },
  created() {
    if (this.isEdit) {
      this.fetchBedSize();
    }
  },
};
</script>

<style scoped>
.bed-size-form {
  padding: 20px;
}
</style> 